<template>
  <div class="TravelDurationForm">
    <h2 class="gl_form-title">
      {{ t(`copy_travel_duration_form-title-${forWhom}`) }}
    </h2>

    <DatePickerComponent
      id="departure-date"
      :label="t(`copy_travel_duration_form_departure-date-${forWhom}`, answers.forWhomPeople)"
      :placeholder="t('copy_travel_duration_form_departure-date-placeholder')"
      v-model="departureDate"
      :datePickerVisible="true"
      :min-date="minDateDeparture"
      :max-date="maxDateDeparture"
      :rules="[required]"
    />
    <DatePickerComponent
      id="return-date"
      ref="returnDatePickerRef"
      :label="t(`copy_travel_duration_form_return-date-${forWhom}`, answers.forWhomPeople)"
      :placeholder="t('copy_travel_duration_form_return-date-placeholder')"
      v-model="returnDate"
      :datePickerVisible="true"
      :min-date="minDateReturn"
      :max-date="maxDateReturn"
      :rules="[required]"
    />
  </div>
</template>

<script lang="ts" setup>
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import type TravelConfigurations from '@/modules/travel/common/models/APP/TravelConfigurations'
  import { addDaysToDate, addYearsToDate } from '@/utils/date.utils'
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type TravelRaterAnswers from '../../models/APP/TravelRaterAnswers'

  const MIN_DATE = addDaysToDate(0)

  const { t } = useI18n()
  const { required } = useRules()

  const returnDatePickerRef = ref<InstanceType<typeof DatePickerComponent> | null>(null)

  defineExpose({ triggerSubmit })
  interface PropsInterface {
    nextForm: String[]
    answers: TravelRaterAnswers
    configurations: TravelConfigurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const forWhom = computed(() => props.answers.forWhom ?? 'me')
  const minDateDeparture = computed(() => {
    const today = new Date(MIN_DATE)
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  })

  const maxDateDeparture = computed(() => addYearsToDate(props.configurations.maxYearsDeparture))

  const departureDate = ref(props.answers.departureDate)
  const returnDate = ref(props.answers.returnDate)

  const minDateReturn = computed(() => {
    const d = new Date(departureDate.value || minDateDeparture.value)
    d.setDate(d.getDate() + 1)
    return d
  })

  const maxDateReturn = computed(() =>
    addYearsToDate(props.configurations.maxYearsReturn, departureDate.value || new Date())
  )

  watch(departureDate, async () => {
    if (returnDate.value && returnDatePickerRef.value) {
      await returnDatePickerRef.value.validate()
    }
  })

  function triggerSubmit() {
    const page = props.nextForm[0]

    const answers = {
      departureDate: departureDate.value,
      startDate: departureDate.value,
      returnDate: returnDate.value
    }
    return { page, answers }
  }
</script>
<style lang="scss" scoped>
  .gl_form-title {
    margin-bottom: $spacing-xl;
  }
</style>
